import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Minus, ShoppingCart, Star, Heart, Share2, Leaf, Flame } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import ProductQRCode from '../components/ProductQRCode';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  
  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProductDetail();
  }, [productId]);

  const fetchProductDetail = async () => {
    try {
      const response = await fetch(`${API}/api/products/${productId}/detail`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProduct(data.product);
        setCategory(data.category);
        setRecommendations(data.recommendations || []);
      } else {
        toast.error('Producto no encontrado');
        navigate('/menu');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    for (let i = 0; i < quantity; i++) {
      addItem({
        product_id: product.product_id,
        name: product.name,
        price: product.display_price || product.price,
        image_url: product.image_url
      });
    }
    
    toast.success(`${quantity}x ${product.name} agregado al carrito`);
  };

  const calculateFinalPrice = () => {
    if (!product) return 0;
    let price = product.display_price || product.price;
    
    if (product.promotion_active) {
      if (product.discount_price) {
        price = product.discount_price;
      } else if (product.discount_percent) {
        price = price * (1 - product.discount_percent / 100);
      }
    }
    
    return price;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const finalPrice = calculateFinalPrice();
  const hasDiscount = finalPrice < product.price;

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-600 hover:text-stone-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver al menú
        </button>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="relative">
            <div className="aspect-square rounded-3xl overflow-hidden bg-white shadow-lg">
              {product.image_url ? (
                <img 
                  src={product.image_url} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-stone-100">
                  <span className="text-6xl">🍽️</span>
                </div>
              )}
            </div>
            
            {/* Badges */}
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              {product.promotion_active && (
                <Badge className="bg-orange-500 text-white border-0 px-3 py-1">
                  {product.promotion_label || `${product.discount_percent}% OFF`}
                </Badge>
              )}
              {product.featured && (
                <Badge className="bg-yellow-500 text-white border-0 px-3 py-1">
                  <Star className="w-3 h-3 mr-1 fill-current" /> Destacado
                </Badge>
              )}
              {product.has_company_price && (
                <Badge className="bg-blue-500 text-white border-0 px-3 py-1">
                  Precio especial
                </Badge>
              )}
            </div>

            {/* Action Buttons */}
            <div className="absolute top-4 right-4 flex flex-col gap-2">
              <button className="w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-stone-50 transition-colors">
                <Heart className="w-5 h-5 text-stone-400" />
              </button>
              <button className="w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-stone-50 transition-colors">
                <Share2 className="w-5 h-5 text-stone-400" />
              </button>
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            {/* Category */}
            {category && (
              <Badge variant="outline" className="text-emerald-700 border-emerald-200">
                {category.name_es}
              </Badge>
            )}

            {/* Name & Description */}
            <div>
              <h1 className="text-3xl font-bold text-stone-900 mb-3">{product.name}</h1>
              <p className="text-stone-600 text-lg leading-relaxed">{product.description}</p>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3">
              <span className="text-4xl font-bold text-emerald-700">
                ${finalPrice.toFixed(2)}
              </span>
              {hasDiscount && (
                <span className="text-xl text-stone-400 line-through">
                  ${product.price.toFixed(2)}
                </span>
              )}
            </div>

            {/* Product Details */}
            <div className="grid grid-cols-2 gap-4 py-4 border-y border-stone-200">
              {product.weight && (
                <div className="text-center">
                  <p className="text-stone-500 text-sm">Porción</p>
                  <p className="font-semibold text-stone-900">{product.weight}</p>
                </div>
              )}
              {product.temperature && (
                <div className="text-center">
                  <p className="text-stone-500 text-sm">Temperatura</p>
                  <p className="font-semibold text-stone-900">{product.temperature}</p>
                </div>
              )}
              {product.calories && (
                <div className="text-center">
                  <p className="text-stone-500 text-sm">Calorías</p>
                  <p className="font-semibold text-stone-900">{product.calories} kcal</p>
                </div>
              )}
              {product.protein_g && (
                <div className="text-center">
                  <p className="text-stone-500 text-sm">Proteína</p>
                  <p className="font-semibold text-stone-900">{product.protein_g}g</p>
                </div>
              )}
            </div>

            {/* Nutritional Info (if available) */}
            {(product.calories || product.protein_g || product.carbs_g || product.fat_g) && (
              <div className="bg-emerald-50 rounded-2xl p-4">
                <h3 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                  <Leaf className="w-4 h-4" /> Información Nutricional
                </h3>
                <div className="grid grid-cols-4 gap-2 text-center text-sm">
                  {product.calories && (
                    <div className="bg-white rounded-lg p-2">
                      <Flame className="w-4 h-4 mx-auto text-orange-500 mb-1" />
                      <p className="font-bold text-stone-900">{product.calories}</p>
                      <p className="text-stone-500 text-xs">kcal</p>
                    </div>
                  )}
                  {product.protein_g && (
                    <div className="bg-white rounded-lg p-2">
                      <p className="font-bold text-stone-900">{product.protein_g}g</p>
                      <p className="text-stone-500 text-xs">Proteína</p>
                    </div>
                  )}
                  {product.carbs_g && (
                    <div className="bg-white rounded-lg p-2">
                      <p className="font-bold text-stone-900">{product.carbs_g}g</p>
                      <p className="text-stone-500 text-xs">Carbos</p>
                    </div>
                  )}
                  {product.fat_g && (
                    <div className="bg-white rounded-lg p-2">
                      <p className="font-bold text-stone-900">{product.fat_g}g</p>
                      <p className="text-stone-500 text-xs">Grasa</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tags */}
            {product.tags && product.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag, i) => (
                  <Badge key={i} variant="secondary" className="bg-stone-100 text-stone-600">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

            {/* Quantity & Add to Cart */}
            <div className="flex items-center gap-4 pt-4">
              <div className="flex items-center gap-3 bg-stone-100 rounded-full px-4 py-2">
                <button 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-8 h-8 flex items-center justify-center text-stone-600 hover:text-stone-900"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-8 text-center font-semibold text-lg">{quantity}</span>
                <button 
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-8 h-8 flex items-center justify-center text-stone-600 hover:text-stone-900"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              
              <Button 
                onClick={handleAddToCart}
                className="flex-1 bg-emerald-700 hover:bg-emerald-800 rounded-full py-6 text-lg"
                data-testid="add-to-cart-detail-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Agregar ${(finalPrice * quantity).toFixed(2)}
              </Button>
            </div>

            {/* QR Code Section */}
            <div className="pt-4">
              <ProductQRCode 
                productId={product.product_id}
                productName={product.name}
                price={finalPrice}
              />
            </div>
          </div>
        </div>

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <section className="mt-16">
            <h2 className="text-2xl font-bold text-stone-900 mb-6">
              Usuarios que compraron esto también compraron...
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {recommendations.map(rec => (
                <div 
                  key={rec.product_id}
                  onClick={() => navigate(`/product/${rec.product_id}`)}
                  className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
                >
                  <div className="aspect-square rounded-xl overflow-hidden bg-stone-100 mb-3">
                    {rec.image_url ? (
                      <img src={rec.image_url} alt={rec.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl">🍽️</div>
                    )}
                  </div>
                  <h3 className="font-medium text-stone-900 text-sm line-clamp-2 mb-1">{rec.name}</h3>
                  <p className="text-emerald-700 font-bold">${rec.price?.toFixed(2)}</p>
                  {rec.promotion_active && (
                    <Badge className="mt-1 bg-orange-100 text-orange-700 border-0 text-xs">
                      {rec.promotion_label || 'Promo'}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
